import { pick } from 'lodash';
import moment from 'moment';
import AtApi from './AtApi';
import StorageService from './StorageService';
import CacheService from './CacheService';

export const GTFS_STORAGE_KEYS = {
  STOPS: 'gtfsStops',
};

/**
 * Fetch the list of stops either from the storage or from the API
 */
export const getStops = async () => {
  const storedStops = await StorageService.getJsonItem(GTFS_STORAGE_KEYS.STOPS);

  if (storedStops?.timestamp && moment().subtract(24, 'hours').isBefore(storedStops.timestamp)) {
    return storedStops.stops;
  }

  const stops = await AtApi.getInstance().getStops();
  if (stops?.length) {
    const processedStops = stops.map(stop => pick(stop, ['location_type', 'parent_station', 'stop_code', 'stop_id', 'stop_lat', 'stop_lon', 'stop_name']));
    StorageService.setItem(GTFS_STORAGE_KEYS.STOPS,
      JSON.stringify({
        stops: processedStops,
        timestamp: moment().valueOf(),
      }));
    return processedStops;
  }
  return Promise.reject(new Error('No GTFS stops found'));
}

export const getStopInfo = async (stopCode, options) => {
  let cacheKey = `fetchStopInfo_${stopCode}`;
  if (options?.startDate) {
    cacheKey = cacheKey.concat(`_${options?.startDate}`);
  }
  const cachedStopInfo = CacheService.get(cacheKey);
  if (cachedStopInfo) {
    return cachedStopInfo;
  }

  const result = await AtApi.getInstance().getStopInfoByCode(stopCode, options);
  if (result) {
    CacheService.set(cacheKey, result);
  }

  return result;
};

export const getStopById = async (stopId) => {
  const cacheKey = `fetchStopWithId_${stopId}`;
  const cachedStop = CacheService.get(cacheKey);
  if (cachedStop) {
    return cachedStop;
  }

  const stops = await getStops();
  const storedStop = stops.find(stopItem => stopItem.stop_id === stopId);

  if (storedStop) {
    CacheService.set(cacheKey, storedStop);
    return storedStop;
  } else {
    return null;
  }
};

export const getStopByCode = async (stopCode) => {
  const cacheKey = `fetchStopWithCode_${stopCode}`;
  const cachedStop = CacheService.get(cacheKey);
  if (cachedStop) {
    return cachedStop;
  }

  const stops = await getStops();
  const storedStop = stops.find(stopItem => stopItem.stop_code === stopCode);

  if (storedStop) {
    CacheService.set(cacheKey, storedStop);
    return storedStop;
  }

  const apiStop = await AtApi.getInstance().getStopByCode(stopCode);
  const firstApiStop = (apiStop && apiStop.length) ? apiStop[0] : null;

  if (!firstApiStop) {
    return null;
  }

  CacheService.set(cacheKey, firstApiStop);
  return firstApiStop;
};

export const getStopsByParentId = async (stopId) => {
  const cacheKey = `fetchStopByParentId_${stopId}`;
  const cachedStops = CacheService.get(cacheKey);
  if (cachedStops) {
    return cachedStops;
  }

  const stops = await getStops();
  const storedStops = stops.filter(stopItem => stopItem.parent_station === stopId);

  if (storedStops) {
    CacheService.set(cacheKey, storedStops);
    return storedStops;
  } else {
    return null;
  }
};

// Note: While the api end point should use id, for now we can also use stopCode to ensure when api
// is not functionally correct we can still get stops.
export const fetchRoutesByStopId = async (stopId) => {
  const cacheKey = `fetchRoutes_${stopId}`;
  const cachedRoutes = CacheService.get(cacheKey);
  if (cachedRoutes) {
    return cachedRoutes;
  }

  const routes = await AtApi.getInstance().getRoutesByStop(stopId);

  CacheService.set(cacheKey, routes);
  return routes;
};

export const getRouteById = async (routeId) => {
  const cacheKey = `fetchRoute_${routeId}`;
  const cachedRoute = CacheService.get(cacheKey);
  if (cachedRoute) {
    return cachedRoute;
  }

  const route = (await AtApi.getInstance().getRouteById(routeId))?.[0] ?? null;
  CacheService.set(cacheKey, route);
  return route;
}